import React, {useState} from 'react';
import {Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar} from '@mui/material';
import {useTranslation} from 'react-i18next';
import ContractTableComponent from '../ContractTableComponent';
import type {Contract} from '../../../types/contract';

interface EmployeeViewContractDialogProps {
    open: boolean;
    selectedEmployeeContract: Contract | null;
    contractsPage: number;
    contractsTotalRecords: number;
    userId: string | number;
    onClose: () => void;
    onContractsPageChange: (page: number) => Promise<void>;
    onConfirmTerminate: (
        userId: string | number,
        contractId: string,
        onContractsPageChange: (page: number) => Promise<void>,
        contractsPage: number,
        setConfirmDialogOpen: React.Dispatch<React.SetStateAction<boolean>>,
        setLoading: React.Dispatch<React.SetStateAction<boolean>>,
        setErrorMessage: React.Dispatch<React.SetStateAction<string>>,
        setSuccessMessage: React.Dispatch<React.SetStateAction<string>>
    ) => Promise<void>;
}

const EmployeeViewContractDialog: React.FC<EmployeeViewContractDialogProps> = ({
                                                                                   open,
                                                                                   selectedEmployeeContract,
                                                                                   contractsPage,
                                                                                   contractsTotalRecords,
                                                                                   userId,
                                                                                   onClose,
                                                                                   onContractsPageChange,
                                                                                   onConfirmTerminate
                                                                               }) => {
    const {t} = useTranslation();
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const handleTerminateContract = () => {
        setConfirmDialogOpen(true);
    };

    const handleConfirmTerminate = async () => {
        if (!selectedEmployeeContract) return;

        await onConfirmTerminate(
            userId,
            selectedEmployeeContract.contractId,
            onContractsPageChange,
            contractsPage,
            setConfirmDialogOpen,
            setLoading,
            setErrorMessage,
            setSuccessMessage
        );
    };

    const handleCloseConfirmDialog = () => {
        setConfirmDialogOpen(false);
    };

    const handleCloseSnackbar = () => {
        setSuccessMessage('');
        setErrorMessage('');
    };


    return (
        <>
            <Dialog open={open} onClose={() => {
            }} disableEscapeKeyDown maxWidth={false} PaperProps={{sx: {width: {xs: 'calc(100% - 32px)', sm: '600px'}, margin: 'auto'}}}>
                <DialogTitle>{t('employee.contract.dialog.viewTitle')}</DialogTitle>
                <DialogContent>
                    <Box sx={{mt: 2}}>
                        <ContractTableComponent
                            selectedEmployeeContract={selectedEmployeeContract}
                            contractsPage={contractsPage}
                            contractsTotalRecords={contractsTotalRecords}
                            onContractsPageChange={onContractsPageChange}
                            sx={{width: '100%'}}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{justifyContent: 'space-between', px: 3, py: 2}}>
                    <Box sx={{display: 'flex', gap: 2}}>
                        <Button
                            variant="outlined"
                            color="error"
                            disabled={selectedEmployeeContract === null || selectedEmployeeContract.status !== 'active' || loading}
                            onClick={handleTerminateContract}
                        >
                            {loading ? t('employee.contract.terminating') : t('employee.contract.terminateButton')}
                        </Button>

                    </Box>
                    <Button onClick={onClose} variant="contained" color="primary">
                        {t('common.actions.close')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialogOpen} onClose={() => {
            }} disableEscapeKeyDown>
                <DialogTitle>{t('employee.contract.dialog.confirmTerminationTitle')}</DialogTitle>
                <DialogContent>
                    <Box sx={{mt: 1}}>
                        {t('employee.contract.confirmTerminationMessage')}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDialog} color="primary">
                        {t('common.actions.cancel')}
                    </Button>
                    <Button onClick={handleConfirmTerminate} color="error" variant="contained">
                        {t('employee.contract.yesTerminate')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success/Error Messages */}
            <Snackbar
                open={!!successMessage}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
            >
                <Alert onClose={handleCloseSnackbar} severity="success" sx={{width: '100%'}}>
                    {successMessage}
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!errorMessage}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
            >
                <Alert onClose={handleCloseSnackbar} severity="error" sx={{width: '100%'}}>
                    {errorMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default EmployeeViewContractDialog;
